import React from 'react';
import * as stylex from '@stylexjs/stylex';
import type { StyleXStyles } from '@stylexjs/stylex';
import { useToggleButtonGroup, useToggleButtonGroupItem, type Key } from 'react-aria';
import { useToggleGroupState, type ToggleGroupState } from 'react-stately';
import { Button, type Size } from './Button';
import type { IconName } from '../images-and-icons/Icon';
import { Field } from '../layout-and-structure/Field';
import { Group } from '../layout-and-structure/Group';
import type { t as Space } from '../theme/Spaces.gen';
import '../theme/Spaces.stylex';
import { Gap } from '../theme/Spaces.stylex';

// Attached items read as a single toolbar. Overlapping borders are resolved
// by z-index, kept numeric so it interpolates along with the button's border
// color transition (a large hover value keeps the item on top until the end).
// Row gap on wrap is emulated with half margins on items, offset by the root,
// since Group's spaceY must stay 'none' to keep items attached horizontally.
const attachedStyles = {
  item: {
    "marginBlock-kqGvvJ": "marginBlock-x5y328k",
    "marginLeft-koQZXg": "marginLeft-xh9i6ac",
    "zIndex-kY2c9j": "zIndex-x1ja2u2z zIndex-xu9xbu2 zIndex-x1r18tff",
    $$css: "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:22"
  },
  itemFirst: {
    "marginLeft-koQZXg": "marginLeft-xj3b58b",
    "borderTopRightRadius-ksF3WI": "borderTopRightRadius-x1a2w583",
    "borderBottomRightRadius-kYm2EN": "borderBottomRightRadius-xjppbhk",
    $$css: "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:27"
  },
  itemMiddle: {
    "borderRadius-kaIpWk": "borderRadius-x2u8bby",
    $$css: "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:32"
  },
  itemLast: {
    "borderTopLeftRadius-kIxVMA": "borderTopLeftRadius-x1ia1hqs",
    "borderBottomLeftRadius-kqGeR4": "borderBottomLeftRadius-xfrllxf",
    $$css: "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:33"
  },
  itemSelected: {
    "zIndex-kY2c9j": "zIndex-x1of6sz5",
    $$css: "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:36"
  }
};
type ItemPosition = 'only' | 'first' | 'middle' | 'last';
const itemPosition = (index: number, count: number): ItemPosition => {
  if (count === 1) return 'only';
  if (index === 0) return 'first';
  if (index === count - 1) return 'last';
  return 'middle';
};
const attachedItemStyle = (position: ItemPosition, isSelected: boolean): StyleXStyles => [attachedStyles.item, position === 'first' && attachedStyles.itemFirst, position === 'middle' && attachedStyles.itemMiddle, position === 'last' && attachedStyles.itemLast, isSelected && attachedStyles.itemSelected];
const spaceFromSize = (size: Size): Space => {
  switch (size) {
    case 'small':
      return 'small';
    case 'medium':
      return 'normal';
    default:
      return 'medium';
  }
};

// MARK: item
// ===========
type Item = {
  value: Key;
  label?: string;
  icon?: IconName;
  endElement?: React.ReactNode;
  disabled?: boolean;
};
type ToggleButtonGroupItemProps = {
  item: Item;
  state: ToggleGroupState;
  size: Size;
  grow: boolean;
  attached: boolean;
  disallowEmptySelection: boolean;
  position: ItemPosition;
};
function ToggleButtonGroupItem({
  item,
  state,
  size,
  grow,
  attached,
  disallowEmptySelection,
  position
}: ToggleButtonGroupItemProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const {
    buttonProps,
    isSelected,
    isDisabled
  } = useToggleButtonGroupItem({
    id: item.value,
    isDisabled: item.disabled
  }, state, ref);
  const {
    role,
    'aria-checked': ariaChecked,
    'aria-pressed': ariaPressed
  } = buttonProps;
  return <Button ref={ref} label={item.label} icon={item.icon} endElement={item.endElement} size={size} grow={grow} active={isSelected} bold={item.label ? isSelected : false} disabled={isDisabled} stylex={attached ? attachedItemStyle(position, isSelected) : undefined} domProps={{
    role,
    'aria-checked': ariaChecked,
    'aria-pressed': ariaPressed
  }} onPress={() => {
    if (disallowEmptySelection && state.selectionMode === 'single' && isSelected) return;
    state.toggleKey(item.value);
  }} />;
}

// MARK: root
// ===========
type ToggleButtonGroupProps = {
  ariaLabel?: string;
  label?: string;
  tooltip?: JSX.Element;
  errorMessage?: string;
  required?: boolean;
  size?: Size;
  grow?: boolean;
  attached?: boolean;
  disallowEmptySelection?: boolean;
  selectionMode?: 'single' | 'multiple';
  items: Item[];
  value: Key[];
  onChange: (value: Key[]) => void;
};
function ToggleButtonGroupComponent({
  ariaLabel,
  label,
  tooltip,
  errorMessage,
  required = false,
  size = 'medium',
  grow = false,
  attached,
  disallowEmptySelection = true,
  selectionMode = 'single',
  items,
  value,
  onChange
}: ToggleButtonGroupProps) {
  const isAttached = attached ?? disallowEmptySelection;
  const props = {
    'aria-label': ariaLabel ?? label,
    selectionMode,
    disallowEmptySelection,
    selectedKeys: value,
    onSelectionChange: (keys: Set<Key>) => onChange([...keys])
  };
  const state = useToggleGroupState(props);
  const ref = React.useRef<HTMLDivElement>(null);
  const {
    groupProps
  } = useToggleButtonGroup(props, state, ref);
  const space = isAttached ? 'none' : spaceFromSize(size);
  if (items.length === 0) return null;
  return <Field label={label} tooltip={tooltip} errorMessage={errorMessage} required={required}>
      <div {...groupProps} {...{
      0: {},
      1: {
        className: "marginBlock-xb22t47",
        "data-style-src": "wino-gc:src/resources/actions/ToggleButtonGroup.tsx:21"
      }
    }[!!isAttached << 0]} ref={ref}>
        <Group grow={grow} grid={grow ? undefined : items.map(() => 'fit-content')} spaceX={space} spaceY={space} alignY="center">
          {items.map((item, index) => <ToggleButtonGroupItem key={item.value} item={item} state={state} size={size} grow={grow} attached={isAttached} disallowEmptySelection={disallowEmptySelection} position={itemPosition(index, items.length)} />)}
        </Group>
      </div>
    </Field>;
}
const MemoizedToggleButtonGroupComponent = React.memo(ToggleButtonGroupComponent);
export { MemoizedToggleButtonGroupComponent as ToggleButtonGroup, type ToggleButtonGroupProps, type Item as ToggleButtonGroupItem };